/**
 * Search tuning: synonym expansion, dimension-format expansion and search redirects.
 *
 * Config comes from the theme settings (Theme settings > Search):
 * - theme.settings.searchSynonyms - one synonym group per line, terms separated by commas.
 * - theme.settings.searchRedirects - one redirect per line, in the format: term >> URL.
 * - theme.settings.searchExpandDimensions - expand "12x24" <-> "12 x 24" style queries.
 *
 * Expanded queries are sent to Shopify as "(original) OR (alternative) ..." so the
 * original query always remains the first alternative (Liquid strips the expansion
 * back out of user-facing text and analytics).
 */

window.SearchTuning = (() => {
  const DIMENSION_RE = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;
  // Queries already using advanced search syntax are passed through untouched.
  const ADVANCED_SYNTAX_RE = /["():]|(^|\s)(OR|AND|NOT)(\s|$)/;
  const MAX_ALTERNATIVES = 8;

  let synonymGroups = null;
  let redirectMap = null;

  /**
   * Normalizes a query for comparison: lowercase, trimmed, collapsed whitespace.
   * @param {string} query - Raw query.
   * @returns {string}
   */
  function normalize(query) {
    return (query || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /**
   * Escapes a string for use within a regular expression.
   * @param {string} str - String to escape.
   * @returns {string}
   */
  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function getSynonymGroups() {
    if (synonymGroups) return synonymGroups;

    synonymGroups = [];
    const raw = (window.theme && theme.settings && theme.settings.searchSynonyms) || '';
    raw.split('\n').forEach((line) => {
      // One-way mapping: "misspelling, variant => canonical" only expands left to right.
      const arrowIndex = line.indexOf('=>');
      if (arrowIndex !== -1) {
        const fromTerms = line.slice(0, arrowIndex).split(',').map(normalize).filter(Boolean);
        const toTerms = line.slice(arrowIndex + 2).split(',').map(normalize).filter(Boolean);
        if (fromTerms.length && toTerms.length) {
          fromTerms.forEach((term) => synonymGroups.push({ term, replacements: toTerms }));
        }
        return;
      }

      // Bidirectional group: every term expands to all the others.
      const terms = line.split(',').map(normalize).filter(Boolean);
      if (terms.length > 1) {
        terms.forEach((term) => {
          synonymGroups.push({ term, replacements: terms.filter((t) => t !== term) });
        });
      }
    });

    return synonymGroups;
  }

  function getRedirectMap() {
    if (redirectMap) return redirectMap;

    redirectMap = {};
    const raw = (window.theme && theme.settings && theme.settings.searchRedirects) || '';
    raw.split('\n').forEach((line) => {
      const sepIndex = line.indexOf('>>');
      if (sepIndex === -1) return;

      const term = normalize(line.slice(0, sepIndex));
      const url = line.slice(sepIndex + 2).trim();
      if (term && url) redirectMap[term] = url;
    });

    return redirectMap;
  }

  /**
   * Returns the redirect URL for a query, or null if none is configured.
   * @param {string} query - Raw query as typed.
   * @returns {string|null}
   */
  function getRedirectUrl(query) {
    return getRedirectMap()[normalize(query)] || null;
  }

  /**
   * Generates alternative phrasings of a dimension query, e.g. "12x24" <-> "12 x 24".
   * The catalog uses both compact and spaced formats in product titles, so both are
   * needed for full coverage.
   * @param {string} query - Normalized query.
   * @returns {string[]}
   */
  function dimensionVariants(query) {
    const match = query.match(DIMENSION_RE);
    if (!match) return [];

    const compact = `${match[1]}x${match[2]}`;
    const spaced = `${match[1]} x ${match[2]}`;
    return [
      normalize(query.replace(DIMENSION_RE, compact)),
      normalize(query.replace(DIMENSION_RE, spaced))
    ];
  }

  /**
   * Generates alternative queries by swapping in synonyms for any group member
   * present in the query (whole word/phrase matches only).
   * @param {string} query - Normalized query.
   * @returns {string[]}
   */
  function synonymVariants(query) {
    const variants = [];

    getSynonymGroups().forEach(({ term, replacements }) => {
      const termRe = new RegExp(`(^|[^a-z0-9])${escapeRegExp(term)}([^a-z0-9]|$)`, 'g');
      if (!termRe.test(query)) return;

      replacements.forEach((replacement) => {
        variants.push(normalize(query.replace(termRe, `$1${replacement}$2`)));
      });
    });

    return variants;
  }

  /**
   * Expands a query into "(original) OR (alternative) ..." covering configured
   * synonyms and dimension format variants. Returns the query unchanged when there
   * is nothing to expand or it already uses advanced search syntax.
   * @param {string} query - Raw query as typed.
   * @returns {string}
   */
  function expandQuery(query) {
    const original = (query || '').trim();
    if (!original || ADVANCED_SYNTAX_RE.test(original)) return original;

    const normalized = normalize(original);
    const alternatives = [normalized];
    const addAlternative = (alt) => {
      if (alt && !alternatives.includes(alt) && alternatives.length < MAX_ALTERNATIVES) {
        alternatives.push(alt);
      }
    };

    if (!window.theme || !theme.settings || theme.settings.searchExpandDimensions !== false) {
      dimensionVariants(normalized).forEach(addAlternative);
    }
    synonymVariants(normalized).forEach(addAlternative);

    // Exact q=vinyl currently ranks Floor Moldings above Vinyl Flooring planks
    // in predictive search (Shopify title-matches "Vinyl … Molding"). OR in
    // the product type so cat-lvp planks outrank moldings. Longer queries
    // (vinyl moldings, luxury vinyl plank) are left to synonym groups.
    if (normalized === 'vinyl') {
      addAlternative('vinyl flooring');
    }

    // Dimension formats inside synonym-substituted queries need covering too.
    alternatives.slice(1).forEach((alt) => {
      dimensionVariants(alt).forEach(addAlternative);
    });

    if (alternatives.length === 1) return original;
    // Keep the original query (as typed) first so it can be recovered for display.
    return [original, ...alternatives.slice(1)].map((alt) => `(${alt})`).join(' OR ');
  }

  /**
   * On direct landings on the search page (bookmarks, ads, predictive suggestion
   * links), applies any configured redirect for the query.
   */
  function handleSearchPageLanding() {
    if (!window.theme || !theme.routes || !theme.routes.search) return;
    if (window.location.pathname !== theme.routes.search) return;

    const query = new URLSearchParams(window.location.search).get('q');
    if (!query) return;

    const redirectUrl = getRedirectUrl(query);
    if (redirectUrl) window.location.replace(redirectUrl);
  }

  handleSearchPageLanding();

  return { normalize, getRedirectUrl, expandQuery };
})();
